import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { SubscriptionReduced } from 'Interfaces/Subscription/SubscriptionPlan';
import { RubricData } from 'Interfaces/Rubric/Rubric';

import {
    DataUserFromGoogle,
    CurrentStep as CurrentStepSignUpProcess,
    ErrorMessagePayload,
    Loading,
    PartnerInformationTemporary,
    LocalRubricUpdateAction,
} from './Interfaces/AppState';

import { appInitialState } from './state';
import { TeamRoster } from 'generated/graphql';

const appSlice = createSlice({
    name: 'app',
    initialState: appInitialState,
    reducers: {
        SET_SHOW_LOGIN_ERROR_MESSAGE: (state) => {
            state.showLoginErrorMessage = true;
        },
        SET_HIDE_LOGIN_ERROR_MESSAGE: (state) => {
            return {
                ...state,
                showLoginErrorMessage: false,
            };
        },
        SET_SHOW_CREATE_ACCOUNT_ERROR_MESSAGE: (
            state,
            action: PayloadAction<ErrorMessagePayload>,
        ) => {
            const { message, activateLinkToLogin } = action.payload;
            return {
                ...state,
                createAccount: {
                    ...state.createAccount,
                    ErrorMessagePayload: {
                        show: true,
                        message,
                        activateLinkToLogin,
                    },
                },
            };
        },
        SET_HIDE_CREATE_ACCOUNT_ERROR_MESSAGE: (state) => {
            return {
                ...state,
                createAccount: {
                    ...state.createAccount,
                    ErrorMessagePayload: {
                        show: false,
                        message: '',
                        activateLinkToLogin: false,
                    },
                },
            };
        },
        SAVE_NAME_TO_CREATE_A_NEW_ACCOUNT: (
            state,
            action: PayloadAction<string>,
        ) => {
            const userName = action.payload;
            return {
                ...state,
                createAccount: {
                    ...state.createAccount,
                    userData: {
                        name: userName,
                    },
                },
            };
        },
        SAVE_DATA_USER_FROM_GOOGLE: (
            state,
            action: PayloadAction<DataUserFromGoogle>,
        ) => {
            const newDataUserFromGoogle = action.payload;
            return {
                ...state,
                signUp: {
                    ...state.signUp,
                    dataUserFromGoogle: newDataUserFromGoogle,
                },
            };
        },
        DELETE_DATA_USER_FROM_GOOGLE: (state) => {
            return {
                ...state,
                signUp: {
                    ...state.signUp,
                    dataUserFromGoogle: {
                        photoURL: '',
                        uid: null,
                        name: '',
                        email: '',
                    },
                },
            };
        },
        UPDATE_CURRENT_STEP_FROM_SIGN_UP_PROCESS: (
            state,
            action: PayloadAction<CurrentStepSignUpProcess>,
        ) => {
            const newCurrentStep = action.payload;
            return {
                ...state,
                signUp: {
                    ...state.signUp,
                    currentStep: newCurrentStep,
                },
            };
        },
        UPDATE_IS_LOADING_TO_CREATE_A_NEW_ACCOUNT: (
            state,
            action: PayloadAction<Loading>,
        ) => {
            const newLoadingValues = action.payload;
            return {
                ...state,
                signUp: {
                    ...state.signUp,
                    loading: newLoadingValues,
                },
            };
        },
        SAVE_PARTNER_DATA_TEMPORARY_SIGN_UP_PROCESS: (
            state,
            action: PayloadAction<PartnerInformationTemporary>,
        ) => {
            const newPartnerName = action.payload;
            return {
                ...state,
                signUp: {
                    ...state.signUp,
                    partnerInformationTemporary: {
                        ...state.signUp.partnerInformationTemporary,
                        ...newPartnerName,
                    },
                },
            };
        },
        DELETE_PARTNER_DATA_TEMPORARY_SIGN_UP_PROCESS: (state) => {
            return {
                ...state,
                signUp: {
                    ...state.signUp,
                    partnerInformationTemporary: {
                        partnerName: '',
                        CRMURL: '',
                        engine: '',
                    },
                },
            };
        },
        SAVE_TEAM_ROSTER_DATA: (
            state,
            action: PayloadAction<Partial<TeamRoster>[]>,
        ) => {
            const newTeamRosterData = action.payload;
            return {
                ...state,
                teamRoster: {
                    ...state.teamRoster,
                    data: newTeamRosterData,
                },
            };
        },
        SAVE_CURRENT_SUBSCRIPTION_PLAN: (
            state,
            action: PayloadAction<SubscriptionReduced>,
        ) => {
            const newSubscriptionDetails = action.payload;
            return {
                ...state,
                teamRoster: {
                    ...state.teamRoster,
                    subscriptionDetails: newSubscriptionDetails,
                },
            };
        },
        ADD_AMOUNT_ON_SUBSCRIPTION_PLAN: (
            state,
            action: PayloadAction<number>,
        ) => {
            const amount = action.payload;
            return {
                ...state,
                teamRoster: {
                    ...state.teamRoster,
                    subscriptionDetails: {
                        ...state.teamRoster.subscriptionDetails,
                        remainingOnPlan:
                            state.teamRoster.subscriptionDetails
                                .remainingOnPlan + amount,
                    },
                },
            };
        },
        SUBTRACT_AMOUNT_ON_SUBSCRIPTION_ON_PLAN: (
            state,
            action: PayloadAction<number>,
        ) => {
            const amount = action.payload;
            return {
                ...state,
                teamRoster: {
                    ...state.teamRoster,
                    subscriptionDetails: {
                        ...state.teamRoster.subscriptionDetails,
                        remainingOnPlan:
                            state.teamRoster.subscriptionDetails
                                .remainingOnPlan - amount,
                    },
                },
            };
        },
        SET_REMAINING_ON_PLAN_OF_SUBSCRIPTION_PLAN: (
            state,
            action: PayloadAction<number>,
        ) => {
            const amount = action.payload;
            return {
                ...state,
                teamRoster: {
                    ...state.teamRoster,
                    subscriptionDetails: {
                        ...state.teamRoster.subscriptionDetails,
                        remainingOnPlan: amount,
                    },
                },
            };
        },
        SETTINGS_SAVE_TYPE_OF_INPUTS_CHANGED: (
            state,
            action: PayloadAction<string[]>,
        ) => {
            const newTypesOfInputsChanged = action.payload;
            return {
                ...state,
                settings: {
                    ...state.settings,
                    typeOfInputsChanged: newTypesOfInputsChanged,
                },
            };
        },
        SETTINGS_WAS_SOME_INPUT_CHANGED: (
            state,
            action: PayloadAction<boolean>,
        ) => {
            const newValue = action.payload;
            return {
                ...state,
                settings: {
                    ...state.settings,
                    wasSomeInputChanged: newValue,
                },
            };
        },
        SETTINGS_WAS_SOME_INPUT_CHANGED_INVALID: (
            state,
            action: PayloadAction<boolean>,
        ) => {
            const wasSomeInputChangedInvalid = action.payload;
            return {
                ...state,
                settings: {
                    ...state.settings,
                    wasSomeInputChangedInvalid,
                },
            };
        },
        SETTINGS_CLEAR_TYPE_OF_INPUTS_CHANGED: (state) => {
            return {
                ...state,
                settings: {
                    ...state.settings,
                    typeOfInputsChanged: [],
                },
            };
        },
        SETTINGS_SHOW_BILLING: (state, action: PayloadAction<boolean>) => {
            const newValue = action.payload;
            return {
                ...state,
                settings: {
                    ...state.settings,
                    showBilling: newValue,
                },
            };
        },
        SETTINGS_HIDE_BILLING: (state, action: PayloadAction<boolean>) => {
            const newValue = action.payload;
            return {
                ...state,
                settings: {
                    ...state.settings,
                    showBilling: newValue,
                },
            };
        },
        SETTINGS_SHOW_UPDATE_CARD_DETAILS: (state) => {
            return {
                ...state,
                settings: {
                    ...state.settings,
                    showUpdateCardDetails: true,
                },
            };
        },
        RUBRIC_UPDATE_CURRENT_USER_COMPONENT: (
            state,
            action: PayloadAction<number>,
        ) => {
            const newCurrentUserComponent = action.payload;
            return {
                ...state,
                rubric: {
                    currentUserComponent: newCurrentUserComponent,
                },
            };
        },
        SAVE_ACTIVE_RUBRICS_LIST: (
            state,
            action: PayloadAction<RubricData[]>,
        ) => {
            const activeRubrics = action.payload;
            return {
                ...state,
                activeRubrics,
            };
        },
        SET_ADDING_NEW_RUBRIC: (state, action) => {
            const isAddingRubric = action.payload as boolean;
            return {
                ...state,
                addingNewRubric: isAddingRubric,
            };
        },
        UPDATE_SAVED_RUBRIC_NAME: (
            state,
            action: PayloadAction<LocalRubricUpdateAction>,
        ) => {
            const updateActionData = action.payload;

            return {
                ...state,
                activeRubrics: state.activeRubrics.map((rubric) => {
                    if (rubric.id === updateActionData.rubricId) {
                        return {
                            ...rubric,
                            name: updateActionData.newRubricName,
                        };
                    }
                    return rubric;
                }),
            };
        },
        SET_CREATING_SINGLE_ACCOUNT: (
            state,
            action: PayloadAction<boolean>,
        ) => {
            const isCreatingSingleAccount = action.payload;

            return {
                ...state,
                creatingSingleAccount: isCreatingSingleAccount,
            };
        },
        SET_SELECTED_RUBRIC_ID: (state, action: PayloadAction<string>) => {
            const newSelectedRubricId = action.payload;
            return {
                ...state,
                selectedRubricId: newSelectedRubricId,
            };
        },
        SET_SELECTED_ASSOCIATES: (state, action: PayloadAction<string[]>) => {
            const selectedAssociates = action.payload;
            return {
                ...state,
                selectedAssociates,
            };
        },
        DEFAULT_WHITELISTED_DOMAINS: (
            state,
            action: PayloadAction<string[]>,
        ) => {
            const newWhitelistedDomains = action.payload;
            return {
                ...state,
                settings: {
                    ...state.settings,
                    defaultWhitelistedDomains: newWhitelistedDomains,
                },
            };
        },
        WHITELISTED_DOMAINS: (state, action: PayloadAction<string[]>) => {
            const whitelistedDomains = action.payload;
            return {
                ...state,
                settings: {
                    ...state.settings,
                    whitelistedDomains,
                },
            };
        },
        WHITELISTED_DOMAINS_TO_REMOVE_FROM_DB: (
            state,
            action: PayloadAction<string>,
        ) => {
            const domainName = action.payload;
            const domainsToRemoveFromDB = [
                ...state.settings.domainsToRemoveFromDB,
                domainName,
            ];
            return {
                ...state,
                settings: {
                    ...state.settings,
                    domainsToRemoveFromDB,
                },
            };
        },
        CLEAR_WHITELISTED_DOMAINS: (state) => {
            const newDefaultDomains = [
                ...state.settings.defaultWhitelistedDomains,
                ...state.settings.whitelistedDomains,
            ];

            return {
                ...state,
                settings: {
                    ...state.settings,
                    defaultWhitelistedDomains: newDefaultDomains,
                    domainsToRemoveFromDB: [],
                    whitelistedDomains: [],
                },
            };
        },
        UNDO_WHITELISTED_DOMAINS_CHANGES: (state) => {
            const newDefaultDomains = [
                ...state.settings.defaultWhitelistedDomains,
                ...state.settings.domainsToRemoveFromDB,
            ];

            return {
                ...state,
                settings: {
                    ...state.settings,
                    defaultWhitelistedDomains: newDefaultDomains,
                    domainsToRemoveFromDB: [],
                    whitelistedDomains: [],
                },
            };
        },
        RESTRICTED_URLS_FOR_WHITELISTED_DOMAINS: (
            state,
            action: PayloadAction<string[]>,
        ) => {
            const restrictedUrlsForWhitelistedDomains = action.payload;
            return {
                ...state,
                settings: {
                    ...state.settings,
                    restrictedUrlsForWhitelistedDomains,
                },
            };
        },
        CALIBRATION_TOGGLE_CREATE_CALIBRATION_COMPONENT: (state) => {
            return {
                ...state,
                calibration: {
                    ...state.calibration,
                    showCreateCalibrationForm:
                        !state.calibration.showCreateCalibrationForm,
                },
            };
        },
        CALIBRATION_UPDATE_STEP_FOR_CREATING_CALIBRATION: (
            state,
            action: PayloadAction<string>,
        ) => {
            const newStep = action.payload;

            return {
                ...state,
                calibration: {
                    ...state.calibration,
                    step: newStep,
                },
            };
        },
        CALIBRATION_SHOW_FIRST_CALIBRATION: (
            state,
            action: PayloadAction<boolean>,
        ) => {
            const showOrHideFirstCalibration = action.payload;

            return {
                ...state,
                calibration: {
                    ...state.calibration,
                    showCreateCalibrationFirstTime: showOrHideFirstCalibration,
                },
            };
        },
        CALIBRATION_SHOW_ONBOARDING_FOR_ADMINS: (
            state,
            action: PayloadAction<boolean>,
        ) => {
            const showOrHideOnboardingCalibration = action.payload;

            return {
                ...state,
                calibration: {
                    ...state.calibration,
                    showOboardingForAdminsThatHasnotCreatedCalibrations:
                        showOrHideOnboardingCalibration,
                },
            };
        },
        CALIBRATION_ONBOARDING_SAVE_CALIBRATION_ID: (
            state,
            action: PayloadAction<string>,
        ) => {
            const calibrationIdSaved = action.payload;

            return {
                ...state,
                calibration: {
                    ...state.calibration,
                    calibrationId: calibrationIdSaved,
                },
            };
        },
        CALIBRATION_ONBOARDING_SHOW_MODAL_AFTER_CREATING: (
            state,
            action: PayloadAction<boolean>,
        ) => {
            const showModalAfterCreating = action.payload;

            return {
                ...state,
                calibration: {
                    ...state.calibration,
                    showModalAfterCreating,
                },
            };
        },
        EDIT_CALIBRATION_SAVE_SELECTED_EVALUATORS: (
            state,
            action: PayloadAction<string[]>,
        ) => {
            const newSelectedAssociates = action.payload;
            return {
                ...state,
                editCalibration: {
                    ...state.editCalibration,
                    selectedEvaluators: newSelectedAssociates,
                },
            };
        },
        TOGGLE_SIDE_DRAWER: (state) => {
            state.showSideDrawer = !state.showSideDrawer;
        },
    },
});

export const { reducer: appReducer, actions: appActions } = appSlice;
